import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DATE_TIME_FORMAT, YES_NO_OPTIONS } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule,
} from 'devextreme-angular';
import {
    AuthService,
    IncidentReportDto,
    IncidentReportsClient,
    CreateIncidentReportCommand,
    UpdateIncidentReportCommand,
    ApproveIncidentReportCommand,
    DataSources,
    IncidentWitnessDto,
    IncidentStepDto,
    IncidentCorrectiveActionDto,
    IncidentStatus,
    PermissionType,
    AccessType,
} from '@services';
import EnumUtils from '../../../../shared/enum-utils';
import notify from 'devextreme/ui/notify';
import { UserAvatarEmailComponent } from '../../../../components/utils/user-avatar-email/user-avatar-email.component';
import { CapitalCasePipe } from '../../../../pipes/capital-case.pipe';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import { ClassificationSectionComponent } from './components/classification-section/classification-section.component';
import { ActivitySectionComponent } from './components/activity-section/activity-section.component';
import { EventsSequenceSectionComponent } from './components/events-sequence-section/events-sequence-section.component';
import { RootCauseActionsSectionComponent } from './components/root-cause-actions-section/root-cause-actions-section.component';
import { ComplianceManagerSectionComponent } from './components/compliance-manager-section/compliance-manager-section.component';
import { EmployeeIncidentDetailsComponent } from './components/employee-incident-details/employee-incident-details.component';
import { WitnessesSectionComponent } from './components/witnesses-section/witnesses-section.component';
import { ApprovalsSectionComponent } from './components/approvals-section/approvals-section.component';
import { NotesComponent, NoteType, } from '../../../../components/library/notes/notes.component';

@Component({
    selector: 'app-incident-report-details',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTextAreaModule,
        UserAvatarEmailComponent,
        DxCheckBoxModule,
        CapitalCasePipe,
        ClassificationSectionComponent,
        ActivitySectionComponent,
        EventsSequenceSectionComponent,
        RootCauseActionsSectionComponent,
        ComplianceManagerSectionComponent,
        EmployeeIncidentDetailsComponent,
        WitnessesSectionComponent,
        ApprovalsSectionComponent,
        NotesComponent,
    ],
    templateUrl: './incident-report-details.component.html',
})
export class IncidentReportDetailsComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private location = inject(Location);
    private router = inject(Router);
    protected auth = inject(AuthService);
    protected dataSources = inject(DataSources);
    private incidentReportsClient = inject(IncidentReportsClient);
    public dateTimeFormat = DATE_TIME_FORMAT;

    @ViewChild('validationGroup', { static: true }) validationGroup: DxValidationGroupComponent;
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    employeesDataSource = this.dataSources.employeeOptions();
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    isReadOnly: boolean = false;
    protected shiftDataSource = this.dataSources.shiftOptions();
    protected incidentId: string | null = null;
    protected incidentReport: IncidentReportDto = new IncidentReportDto();
    protected readonly NoteType = NoteType;
    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;

    protected yesOrNoOptions = YES_NO_OPTIONS;

    ngOnInit(): void {
        this.incidentId = this.route.snapshot.paramMap.get('id');
        if (this.incidentId) {
            this.isReadOnly = !this.auth.checkPermission(
                this.PermissionType.IncidentReportApproval,
                this.AccessType.Manage,
            );
            this.loadIncidentReport();
        } else {
            this.initializeNewIncidentReport();
        }
    }

    private initializeNewIncidentReport() {
        this.incidentReport = new IncidentReportDto();
        this.incidentReport.witnesses = [
            IncidentWitnessDto.fromJS({
                employeeId: null,
                externalName: '',
                isNotEmployee: false,
            }),
        ];
        this.incidentReport.steps = [
            IncidentStepDto.fromJS({ description: '', stepNumber: 0 }),
        ];
        this.incidentReport.correctiveActions = [
            IncidentCorrectiveActionDto.fromJS({
                actionItem: '',
                responsiblePerson: '',
                targetDate: null,
                completionDate: null,
            }),
        ];
        this.incidentReport.isDrivingEquipment = false;
        this.incidentReport.isLifting = false;
        this.incidentReport.isClimbing = false;
        this.incidentReport.isStanding = false;
        this.incidentReport.isRunning = false;
        this.incidentReport.isCarrying = false;
        this.incidentReport.isSitting = false;
        this.incidentReport.isLayingDown = false;
        this.incidentReport.isWalking = false;
        this.incidentReport.isPushingOrPulling = false;
        this.incidentReport.isKneeling = false;
        this.incidentReport.isStretchingOrReclining = false;
        this.incidentReport.isOther = false;
        this.incidentReport.workOrderSent = false;
        this.incidentReport.reTrainingNeeded = false;
        this.incidentReport.isSafetyHazard = false;
        this.incidentReport.isHealthHazard = false;
        this.incidentReport.isEnvironmentalHazard = false;
    }

    private loadIncidentReport() {
        this.isLoading = true;
        this.incidentReportsClient
            .getIncidentReport(this.incidentId!)
            .subscribe({
                next: async (data) => {
                    this.incidentReport = data;
                    this.isLoading = false;
                },
                error: () => {
                    notify('Failed to load incident report', 'error', 3000);
                    this.isLoading = false;
                    this.location.back();
                },
            });
    }

    validateYesNo(e: any) {
        return e.value === true || e.value === false;
    }

    protected getBadgeClass(status: IncidentStatus): string {
        return EnumUtils.getIncidentStatusBadgeClass(status);
    }

    get showApproveButton(): boolean {
        if(this.isLoading)
            return false;

        return this.incidentId && !this.isReadOnly && this.incidentReport.canBeApproved;
    }

    save(isApproval: boolean = false) {
        const result = this.validationGroup.instance.validate();

        if (!result.isValid) {
            notify(
                {
                    message: 'Please fix validation errors',
                    position: 'bottom center',
                },
                'error',
                3000,
            );
            return;
        }

        this.isSubmitting = true;

        const command = this.incidentId
            ? isApproval
                ? new ApproveIncidentReportCommand({ ...this.incidentReport })
                : new UpdateIncidentReportCommand({ ...this.incidentReport })
            : new CreateIncidentReportCommand({ ...this.incidentReport });

        const request$ = this.incidentId
            ? isApproval
                ? this.incidentReportsClient.approveIncidentReport(command)
                : this.incidentReportsClient.updateIncidentReport(command)
            : this.incidentReportsClient.createIncidentReport(command);

        request$.subscribe({
            next: async (id) => {
                if (this.incidentId === null && this.notesComponent) {
                    await this.notesComponent.saveQueuedNotes(id);
                }

                notify(
                    this.incidentId
                        ? 'Incident report updated successfully'
                        : 'Incident report created successfully',
                    'success',
                    3000,
                );
                this.isSubmitting = false;
                if (this.incidentId) {
                    this.loadIncidentReport();
                } else {
                    await this.router.navigate(['/incident-report/edit', id], {
                        replaceUrl: true,
                    });
                }
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message ||
                    (this.incidentId
                        ? 'Failed to update incident report'
                        : 'Failed to create incident report');
                notify(msg, 'error', 3000);
                this.isSubmitting = false;
            },
        });
    }

    cancel() {
        this.location.back();
    }
}
